/**
 * Audio player for Supernal TTS Widget
 */

import { AnalyticsManager } from '../core/analytics';

export class AudioPlayer {
  private currentAudio: HTMLAudioElement | null = null;
  private currentButton: HTMLButtonElement | null = null;
  private progressUpdateInterval: number | null = null;
  private clientSideSpeed: boolean;
  private analyticsManager: AnalyticsManager;

  // Analytics tracking state
  private audioStartTime: number = 0;
  private currentAudioHash?: string;
  private currentAudioDuration: number = 0;

  constructor(clientSideSpeed: boolean, analyticsManager: AnalyticsManager) {
    this.clientSideSpeed = clientSideSpeed;
    this.analyticsManager = analyticsManager;
  }

  /**
   * Play audio with optional progress bar tracking
   */
  public async playAudio(
    audioUrl: string,
    button: HTMLButtonElement,
    onStateChange: (state: string) => void,
    onProgressUpdate: (audio: HTMLAudioElement, progressBar: HTMLElement) => void,
    progressBar?: HTMLElement | null,
    playbackSpeed?: number,
    audioHash?: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;
      this.currentButton = button;

      // Enable pitch preservation for client-side speed control
      // This uses native browser time-stretching to maintain pitch quality
      if (this.clientSideSpeed) {
        audio.preservesPitch = true;
        // Fallback for older browsers
        (audio as any).mozPreservesPitch = true;
        (audio as any).webkitPreservesPitch = true;
      }

      // Set playback rate if provided (for client-side speed control)
      if (playbackSpeed !== undefined && this.clientSideSpeed) {
        audio.playbackRate = playbackSpeed;
      }

      // Clear any existing interval before starting new one
      if (this.progressUpdateInterval) {
        clearInterval(this.progressUpdateInterval);
        this.progressUpdateInterval = null;
      }

      audio.addEventListener('loadstart', () => {
        if (this.currentAudio === audio) {
          onStateChange('loading');
        }
      });

      audio.addEventListener('canplay', () => {
        if (this.currentAudio === audio) {
          onStateChange('playing');
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('Audio play failed:', error);
              if (this.currentAudio === audio) {
                onStateChange('error');
                this.currentAudio = null;
                this.currentButton = null;
                reject(new Error('Audio playback failed'));
              }
            });
          }

          // Track audio play event
          this.audioStartTime = Date.now();
          this.currentAudioHash = audioHash;
          this.currentAudioDuration = audio.duration * 1000; // Convert to ms
          if (audioHash) {
            this.analyticsManager.trackEvent('play', {
              audioHash: audioHash
            });
          }

          // Start progress bar updates using global interval
          if (progressBar) {
            progressBar.classList.add('playing');
            this.progressUpdateInterval = window.setInterval(() => {
              onProgressUpdate(audio, progressBar);
            }, 100);
          }
        }
      });

      audio.addEventListener('ended', () => {
        if (this.currentAudio === audio) {
          // Track completion event
          const playTime = Date.now() - this.audioStartTime;
          if (audioHash) {
            this.analyticsManager.trackEvent('complete', {
              audioHash: audioHash,
              playTime: playTime,
              completionRate: 1.0 // 100% completed
            });
          }

          onStateChange('ready');
          this.currentAudio = null;
          this.currentButton = null;
          this.currentAudioHash = undefined;

          // Clear progress bar updates
          if (this.progressUpdateInterval) {
            clearInterval(this.progressUpdateInterval);
            this.progressUpdateInterval = null;
          }
          if (progressBar) {
            progressBar.classList.remove('playing');
            // Reset progress bar
            const slider = progressBar.querySelector<HTMLInputElement>('.supernal-tts-progress-slider');
            if (slider) slider.value = '0';
            const timeDisplay = progressBar.querySelector('.supernal-tts-time-display');
            if (timeDisplay) timeDisplay.innerHTML = '<span class="current">0:00</span> / <span class="duration">0:00</span>';
          }

          resolve();
        }
      });

      audio.addEventListener('error', (e) => {
        if (this.currentAudio === audio) {
          onStateChange('error');
          this.currentAudio = null;
          this.currentButton = null;

          // Clear progress bar updates
          if (this.progressUpdateInterval) {
            clearInterval(this.progressUpdateInterval);
            this.progressUpdateInterval = null;
          }
          if (progressBar) {
            progressBar.classList.remove('playing');
          }

          reject(new Error('Audio playback failed'));
        }
      });

      audio.load();
    });
  }

  /**
   * Play audio starting from a specific position
   */
  public async playAudioFromPosition(
    audioUrl: string,
    button: HTMLButtonElement,
    onStateChange: (state: string) => void,
    onProgressUpdate: (audio: HTMLAudioElement, progressBar: HTMLElement) => void,
    progressBar: HTMLElement | null | undefined,
    positionPercent: number,
    audioHash?: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(audioUrl);
      this.currentAudio = audio;
      this.currentButton = button;

      // Enable pitch preservation for client-side speed control
      // This uses native browser time-stretching to maintain pitch quality
      if (this.clientSideSpeed) {
        audio.preservesPitch = true;
        // Fallback for older browsers
        (audio as any).mozPreservesPitch = true;
        (audio as any).webkitPreservesPitch = true;
      }

      // Clear any existing interval before starting new one
      if (this.progressUpdateInterval) {
        clearInterval(this.progressUpdateInterval);
        this.progressUpdateInterval = null;
      }

      let hasSeekCompleted = false;

      audio.addEventListener('loadstart', () => {
        if (this.currentAudio === audio) {
          onStateChange('loading');
        }
      });

      audio.addEventListener('loadedmetadata', () => {
        // Once we know the duration, seek to the saved position
        if (this.currentAudio === audio && !hasSeekCompleted && positionPercent > 0) {
          audio.currentTime = audio.duration * positionPercent;
          hasSeekCompleted = true;
        }
      });

      audio.addEventListener('canplay', () => {
        if (this.currentAudio === audio) {
          // Seek again if metadata wasn't ready earlier
          if (!hasSeekCompleted && positionPercent > 0 && audio.duration > 0) {
            audio.currentTime = audio.duration * positionPercent;
            hasSeekCompleted = true;
          }

          onStateChange('playing');
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('Audio play failed:', error);
              if (this.currentAudio === audio) {
                onStateChange('error');
                this.currentAudio = null;
                this.currentButton = null;
                reject(new Error('Audio playback failed'));
              }
            });
          }

          // Track audio play event
          this.audioStartTime = Date.now();
          this.currentAudioHash = audioHash;
          this.currentAudioDuration = audio.duration * 1000; // Convert to ms
          if (audioHash) {
            this.analyticsManager.trackEvent('play', {
              audioHash: audioHash
            });
          }

          // Start progress bar updates using global interval
          if (progressBar) {
            progressBar.classList.add('playing');
            this.progressUpdateInterval = window.setInterval(() => {
              onProgressUpdate(audio, progressBar);
            }, 100);
          }
        }
      });

      audio.addEventListener('ended', () => {
        if (this.currentAudio === audio) {
          // Track completion event
          const playTime = Date.now() - this.audioStartTime;
          if (audioHash) {
            this.analyticsManager.trackEvent('complete', {
              audioHash: audioHash,
              playTime: playTime,
              completionRate: 1.0 // 100% completed
            });
          }

          onStateChange('ready');
          this.currentAudio = null;
          this.currentButton = null;
          this.currentAudioHash = undefined;

          // Clear progress bar updates
          if (this.progressUpdateInterval) {
            clearInterval(this.progressUpdateInterval);
            this.progressUpdateInterval = null;
          }
          if (progressBar) {
            progressBar.classList.remove('playing');
            // Reset progress bar
            const slider = progressBar.querySelector<HTMLInputElement>('.supernal-tts-progress-slider');
            if (slider) slider.value = '0';
            const timeDisplay = progressBar.querySelector('.supernal-tts-time-display');
            if (timeDisplay) timeDisplay.innerHTML = '<span class="current">0:00</span> / <span class="duration">0:00</span>';
          }

          resolve();
        }
      });

      audio.addEventListener('error', (e) => {
        if (this.currentAudio === audio) {
          onStateChange('error');
          this.currentAudio = null;
          this.currentButton = null;

          // Clear progress bar updates
          if (this.progressUpdateInterval) {
            clearInterval(this.progressUpdateInterval);
            this.progressUpdateInterval = null;
          }
          if (progressBar) {
            progressBar.classList.remove('playing');
          }

          reject(new Error('Audio playback failed'));
        }
      });

      audio.load();
    });
  }

  /**
   * Pause current audio
   */
  public pauseAudio(onStateChange: (state: string) => void): void {
    if (this.currentAudio && !this.currentAudio.paused) {
      this.currentAudio.pause();
    }

    if (this.currentButton) {
      onStateChange('paused');
    }
  }

  /**
   * Resume paused audio
   */
  public resumeAudio(onStateChange: (state: string) => void): void {
    if (this.currentAudio && this.currentAudio.paused) {
      const playPromise = this.currentAudio.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          if (this.currentButton) {
            onStateChange('playing');
          }
        }).catch(error => {
          console.error('Audio resume failed:', error);
          if (this.currentButton) {
            onStateChange('error');
          }
        });
      }
    }
  }

  /**
   * Stop current audio
   */
  public stopAudio(onStateChange: (state: string) => void): void {
    // Track stop event if audio was playing
    if (this.currentAudio && this.currentAudioHash) {
      const playTime = Date.now() - this.audioStartTime;
      const completionRate = this.currentAudioDuration > 0
        ? (this.currentAudio.currentTime * 1000) / this.currentAudioDuration
        : 0;

      this.analyticsManager.trackEvent('stop', {
        audioHash: this.currentAudioHash,
        playTime: playTime,
        completionRate: Math.min(completionRate, 1.0) // Cap at 1.0
      });
    }

    // Clear interval first
    if (this.progressUpdateInterval) {
      clearInterval(this.progressUpdateInterval);
      this.progressUpdateInterval = null;
    }

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }

    if (this.currentButton) {
      onStateChange('ready');
      this.currentButton = null;
    }

    this.currentAudioHash = undefined;
  }

  /**
   * Clean stop audio with proper progress bar cleanup
   * Used when regenerating audio with different parameters
   */
  public cleanStopAudio(onStateChange: (state: string) => void, progressBar?: HTMLElement | null): void {
    // Clear interval first
    if (this.progressUpdateInterval) {
      clearInterval(this.progressUpdateInterval);
      this.progressUpdateInterval = null;
    }

    // Stop and clear audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }

    // Reset button state
    if (this.currentButton) {
      onStateChange('ready');
      this.currentButton = null;
    }

    // Clean up progress bar immediately
    if (progressBar) {
      progressBar.classList.remove('playing');
      const slider = progressBar.querySelector<HTMLInputElement>('.supernal-tts-progress-slider');
      if (slider) {
        slider.value = '0';
      }
      const timeDisplay = progressBar.querySelector('.supernal-tts-time-display');
      if (timeDisplay) {
        timeDisplay.innerHTML = '<span class="current">0:00</span> / <span class="duration">0:00</span>';
      }
    }
  }

  /**
   * Clean stop audio but keep progress bar at current position (for voice/speed changes)
   */
  public cleanStopAudioKeepProgress(onStateChange: (state: string) => void): void {
    // Clear any existing progress update intervals first
    if (this.progressUpdateInterval) {
      clearInterval(this.progressUpdateInterval);
      this.progressUpdateInterval = null;
    }

    // Stop and clear audio
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }

    // Reset button state but don't clear currentButton yet (we're regenerating)
    if (this.currentButton) {
      onStateChange('loading');
    }

    // Keep progress bar visible but stop updates
    // Progress position is maintained by not resetting the slider value
  }

  /**
   * Set playback rate for current audio
   */
  public setPlaybackRate(rate: number): void {
    if (this.currentAudio && this.clientSideSpeed) {
      this.currentAudio.playbackRate = rate;
    }
  }

  /**
   * Get current playback position as percentage
   */
  public getCurrentPosition(): number {
    if (this.currentAudio && this.currentAudio.duration > 0) {
      return this.currentAudio.currentTime / this.currentAudio.duration;
    }
    return 0;
  }

  /**
   * Check if currently playing on a specific button
   */
  public isPlayingOn(button: HTMLButtonElement): boolean {
    return this.currentButton === button && this.currentAudio !== null;
  }

  /**
   * Check if paused on a specific button
   */
  public isPausedOn(button: HTMLButtonElement): boolean {
    return this.currentButton === button && this.currentAudio !== null && this.currentAudio.paused;
  }

  /**
   * Get current button
   */
  public getCurrentButton(): HTMLButtonElement | null {
    return this.currentButton;
  }

  /**
   * Get current audio element
   */
  public getCurrentAudio(): HTMLAudioElement | null {
    return this.currentAudio;
  }
}
